import { Track } from '../types';

export const DEFAULT_LOCALE = 'en-US';
export const LOCALE_STORAGE_KEY = 'napster-preferred-locale';

// Available locales - can be expanded as needed
export const AVAILABLE_LOCALES = [
  { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
  { code: 'es-ES', name: 'Español (España)', flag: '🇪🇸' },
  { code: 'es-MX', name: 'Español (México)', flag: '🇲🇽' },
  { code: 'fr-FR', name: 'Français', flag: '🇫🇷' },
  { code: 'de-DE', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it-IT', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'pt-PT', name: 'Português (Portugal)', flag: '🇵🇹' },
  { code: 'ja-JP', name: '日本語', flag: '🇯🇵' },
  { code: 'ko-KR', name: '한국어', flag: '🇰🇷' },
  { code: 'zh-CN', name: '中文 (简体)', flag: '🇨🇳' },
  { code: 'ar-SA', name: 'العربية', flag: '🇸🇦' },
];

/**
 * Get the preferred locale from localStorage or default
 */
export const getPreferredLocale = (): string => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return stored || DEFAULT_LOCALE;
  } catch (error) {
    console.warn('Failed to get preferred locale from localStorage:', error);
    return DEFAULT_LOCALE;
  }
};

/**
 * Save the preferred locale to localStorage
 */
export const setPreferredLocale = (locale: string): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch (error) {
    console.warn('Failed to save preferred locale to localStorage:', error);
  }
};

/**
 * Get the URL for a track in the specified locale
 * Prefers MP3 over WAV when both are available for the same locale
 * Falls back to default locale or first available URL if locale not found
 */
export const getTrackUrlForLocale = (
  track: Track,
  locale: string = DEFAULT_LOCALE,
): string => {
  // Handle backward compatibility - if track has direct url property
  if (track.url && (!track.urls || track.urls.length === 0)) {
    return track.url;
  }

  // Handle new multi-locale structure
  if (track.urls && track.urls.length > 0) {
    // Try to find exact locale match with preference for MP3
    const localeMatches = track.urls.filter(
      (urlObj) => urlObj.locale === locale,
    );
    if (localeMatches.length > 0) {
      // Prefer MP3 over WAV if both are available
      const mp3Match = localeMatches.find(
        (urlObj) => urlObj.extension === '.mp3',
      );
      if (mp3Match) {
        return mp3Match.url;
      }
      // Fall back to first match for this locale (likely WAV)
      return localeMatches[0].url;
    }

    // Try to find default locale with preference for MP3
    const defaultMatches = track.urls.filter(
      (urlObj) => urlObj.locale === DEFAULT_LOCALE,
    );
    if (defaultMatches.length > 0) {
      // Prefer MP3 over WAV if both are available
      const mp3Match = defaultMatches.find(
        (urlObj) => urlObj.extension === '.mp3',
      );
      if (mp3Match) {
        return mp3Match.url;
      }
      // Fall back to first match for default locale (likely WAV)
      return defaultMatches[0].url;
    }

    // Fall back to first available URL with preference for MP3
    const mp3Fallback = track.urls.find(
      (urlObj) => urlObj.extension === '.mp3',
    );
    if (mp3Fallback) {
      return mp3Fallback.url;
    }

    // Final fallback - first available URL
    return track.urls[0].url;
  }

  // Fallback - should not happen in normal cases
  console.warn('Track has no URL available:', track);
  return '';
};

/**
 * Get available locales for a track
 */
export const getAvailableLocalesForTrack = (track: Track): string[] => {
  if (track.urls && track.urls.length > 0) {
    return track.urls.map((urlObj) => urlObj.locale);
  }

  // If using old structure, assume default locale
  if (track.url) {
    return [DEFAULT_LOCALE];
  }

  return [];
};

/**
 * Check if a locale is available for a track
 */
export const isLocaleAvailableForTrack = (
  track: Track,
  locale: string,
): boolean => {
  const availableLocales = getAvailableLocalesForTrack(track);
  return availableLocales.includes(locale);
};

/**
 * Get locale display name
 */
export const getLocaleDisplayName = (locale: string): string => {
  const localeInfo = AVAILABLE_LOCALES.find((l) => l.code === locale);
  return localeInfo ? localeInfo.name : locale;
};

/**
 * Get locale flag emoji
 */
export const getLocaleFlag = (locale: string): string => {
  const localeInfo = AVAILABLE_LOCALES.find((l) => l.code === locale);
  return localeInfo ? localeInfo.flag : '🌐';
};

/**
 * Update URL with locale parameter for sharing
 */
export const addLocaleToUrl = (url: string, locale: string): string => {
  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('locale', locale);
    return urlObj.toString();
  } catch (error) {
    console.warn('Failed to add locale to URL:', error);
    return url;
  }
};

/**
 * Get locale from URL parameters
 */
export const getLocaleFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('locale');
  } catch (error) {
    console.warn('Failed to get locale from URL:', error);
    return null;
  }
};
